import styled from 'styled-components'
import React from 'react'
import { type MeetingProvider } from '../../../../common/meetingProviders'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import './SourceIcon.scss'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../../common/integrations'

const SourceIconContainer = styled.div<{ $background?: string; $border?: string }>`
  display: inline-flex;
  padding: 0.5rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.25rem;
  background: ${({ $background }) => $background ?? 'var(--zocks-gray-200, #e9ecef)'};
  border: ${({ $border }) => $border ?? 'none'};
  box-sizing: border-box;
  svg {
    width: 2rem;
    height: 2rem;
  }
  i {
    width: 2rem;
    height: 2rem;
    font-size: 2rem;
  }
  img {
    width: 2rem;
    height: 2rem;
    object-fit: contain;
  }
`
type InternalSource = 'import' | 'voice-memo' | 'in-person' | 'voice-memo-desktop' | 'voice-memo-mobile'
export type Source = MeetingProvider | InternalSource

const iconMap: { [key in Source]: React.ReactNode } = {
  zoom: <IntegrationIcon serviceId={IntegrationServiceId.ZOOM} />,
  meet: <IntegrationIcon serviceId={IntegrationServiceId.MEET} />,
  teams: <IntegrationIcon serviceId={IntegrationServiceId.TEAMS} />,
  phone: <i className='bi bi-telephone-fill' />,
  import: <i className='bi bi-upload' />,
  'voice-memo': <i className='bi bi-person-fill' />,
  'in-person': <i className='bi bi-person-fill' />,
  'voice-memo-desktop': <i className='bi bi-person-fill' />,
  'voice-memo-mobile': (
    <div className='position-relative mobile-voice-memo-icon'>
      <i className='bi bi-person-fill'></i>
      <div className='phone-icon-container d-block position-absolute rounded-1 border-2 border-white'>
        <i className='bi bi-phone'></i>
      </div>
    </div>
  ),
  webex: <IntegrationIcon serviceId={IntegrationServiceId.WEBEX} />,
  'phone-ringcentral': <IntegrationIcon serviceId={IntegrationServiceId.RINGCENTRAL} />,
  'phone-goto': <IntegrationIcon serviceId={IntegrationServiceId.GOTO} preferWordmark />,
}
const tooltipTitleMap: { [key in Source]: string } = {
  zoom: 'Zoom',
  meet: 'Google Meet',
  teams: 'Microsoft Teams',
  phone: 'Phone',
  import: 'Imported',
  'voice-memo': 'Zocks',
  'in-person': 'In Person',
  'voice-memo-desktop': 'Zocks (Desktop)',
  'voice-memo-mobile': 'Zocks (Mobile)',
  webex: 'Webex',
  'phone-ringcentral': 'RingCentral',
  'phone-goto': 'GoTo',
}

interface SourceIconStyle {
  background: string
  border: string
}

const SOURCE_ICON_STYLE_MAP: Partial<Record<Source, SourceIconStyle>> = {
  'phone-goto': { background: '#fff', border: '1px solid #E9ECEF' },
}

export const SourceIcon: React.FC<{ source: Source }> = ({ source }) => {
  const containerStyle = source ? SOURCE_ICON_STYLE_MAP[source] : undefined
  return (
    <SourceIconContainer $background={containerStyle?.background} $border={containerStyle?.border}>
      <Tooltip title={source ? tooltipTitleMap[source] : 'In Person'} placement='top'>
        {source ? iconMap[source] : <i className='bi bi-person-fill' />}
      </Tooltip>
    </SourceIconContainer>
  )
}
